import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll position restoration component specifically for infinite scroll pages
 * Uses an aggressive strategy: checks page height and attempts restoration every 100ms
 * Maximum 20 attempts (2 seconds) to ensure accurate restoration even with async loading
 * 
 * Improvements:
 * - Detects user active scrolling to avoid interrupting user actions
 * - Delays scroll reset until navigation completes to prevent flickering
 * - Unified scroll operation logic to reduce conflicts
 * - Handles anchor navigation properly
 */
const InfiniteScrollRestoration = () => {
  const location = useLocation();
  const fullPath = location.pathname + location.search;
  const locationKey = location.key;
  const previousFullPath = useRef(fullPath);
  const previousLocationKey = useRef(locationKey);
  const isPopStateNavigation = useRef(false);
  const restoreTimeoutRef = useRef(null);
  const heightCheckInterval = useRef(null);
  const isRestoringScroll = useRef(false);
  const userScrolledManually = useRef(false);
  const skipScrollSaving = useRef(false); // Flag to skip scroll position saving during active navigation
  const justCompletedActiveNav = useRef(false); // Track if we just completed active navigation to ignore subsequent popstate
  const hasCheckedInitialScroll = useRef(false); // Track if we've checked initial scroll position on page load
  
  // User scrolling detection
  const isUserScrolling = useRef(false);
  const userScrollTimeout = useRef(null);
  const lastUserScrollTime = useRef(0);
  const scrollLockTimeout = useRef(null);
  
  // Navigation state
  const isNavigating = useRef(false);
  const navigationStartTime = useRef(0);
  
  // Protection period after back navigation
  const isInProtectionPeriod = useRef(false);
  const protectionPeriodTimeout = useRef(null);
  
  // Immediate scroll lock to prevent browser default restoration
  const isScrollLocked = useRef(false);
  
  // Track if this is the first mount (page load/refresh)
  // Initialize based on whether we have a previousFullPath (if not, it's first mount)
  const isFirstMount = useRef(!previousFullPath.current);

  /**
   * Unified scroll reset function
   * Checks if user is actively scrolling before resetting to avoid interruption
   */
  const resetScrollToTop = (immediate = false) => {
    // If user is actively scrolling, delay the reset
    if (!immediate && isUserScrolling.current) {
      // Clear any existing timeout
      if (scrollLockTimeout.current) {
        clearTimeout(scrollLockTimeout.current);
      }
      // Wait for user to stop scrolling (150ms after last scroll event)
      scrollLockTimeout.current = setTimeout(() => {
        if (!isUserScrolling.current) {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }, 150);
      return;
    }
    
    // Immediate reset (for critical cases)
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  /**
   * Check if URL has anchor/hash
   */
  const hasAnchor = (path) => {
    return path.includes('#') || location.hash;
  };

  // Function to restore scroll position
  const restoreScrollPosition = (targetPath) => {
    const savedScroll = parseInt(sessionStorage.getItem(`scroll_${targetPath}`) || '0', 10);
    
    // CRITICAL: Check if user is scrolling or has scrolled before starting restoration
    if (isUserScrolling.current || userScrolledManually.current) {
      return;
    }
    
    // CRITICAL: Check if we're still in protection period
    if (isInProtectionPeriod.current) {
      // If user scrolled during protection period, cancel restoration
      if (userScrolledManually.current || isUserScrolling.current) {
        return;
      }
    }
    
    if (savedScroll > 0) {
      // CRITICAL: Release scroll lock when restoration starts
      // This allows scroll events to be processed during restoration
      isScrollLocked.current = false;
      // Mark as restoring
      isRestoringScroll.current = true;
      userScrolledManually.current = false;
      
      let attemptCount = 0;
      const maxAttempts = 10; // 10 attempts * 100ms = 1 second max
      let lastHeight = 0;
      let stableHeightCount = 0;
      
      const restoreScroll = () => {
        // If user manually scrolled, stop restoration immediately
        if (userScrolledManually.current) {
          if (heightCheckInterval.current) {
            clearInterval(heightCheckInterval.current);
            heightCheckInterval.current = null;
          }
          isRestoringScroll.current = false;
          return;
        }
        
        // CRITICAL: Check if user is actively scrolling - if so, stop restoration immediately
        if (isUserScrolling.current) {
          userScrolledManually.current = true; // Mark as manual scroll to prevent future restoration
          if (heightCheckInterval.current) {
            clearInterval(heightCheckInterval.current);
            heightCheckInterval.current = null;
          }
          isRestoringScroll.current = false;
          return;
        }
        
        attemptCount++;
        const currentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        
        // Check if height is stable
        if (currentHeight === lastHeight) {
          stableHeightCount++;
        } else {
          stableHeightCount = 0;
          lastHeight = currentHeight;
        }
        
        // If target position is within current page range
        if (savedScroll <= currentHeight - windowHeight + 200) {
          // CRITICAL: Check again before executing scrollTo to ensure user hasn't started scrolling
          if (isUserScrolling.current || userScrolledManually.current) {
            if (heightCheckInterval.current) {
              clearInterval(heightCheckInterval.current);
              heightCheckInterval.current = null;
            }
            isRestoringScroll.current = false;
            return;
          }
          
          window.scrollTo({
            top: savedScroll,
            left: 0,
            behavior: 'instant'
          });
          
          // Verify actual scroll position
          const actualScroll = window.scrollY || window.pageYOffset;
          const diff = Math.abs(actualScroll - savedScroll);
          
          // Restoration successful if error < 50px and height stable 3 times
          // Or error < 200px and height stable 5 times (small deviation from Footer rendering)
          if ((diff < 50 && stableHeightCount >= 3) || (diff < 200 && stableHeightCount >= 5)) {
            if (heightCheckInterval.current) {
              clearInterval(heightCheckInterval.current);
              heightCheckInterval.current = null;
            }
            isRestoringScroll.current = false;
          } else if (attemptCount >= maxAttempts) {
            if (heightCheckInterval.current) {
              clearInterval(heightCheckInterval.current);
              heightCheckInterval.current = null;
            }
            isRestoringScroll.current = false;
          }
        } else {
          // Page height insufficient, wait for content to load before scrolling
          // CRITICAL: Don't immediately scroll to bottom - wait a bit to see if content loads
          // This prevents the flash from 0 to bottom position
          if (attemptCount < 3) {
            // First few attempts: just wait, don't scroll yet
            // This gives time for content to load without causing flash
            return;
          }
          
          // After a few attempts, if height still insufficient, scroll near bottom to trigger load more
          // CRITICAL: Check again before executing scrollTo to ensure user hasn't started scrolling
          if (isUserScrolling.current || userScrolledManually.current) {
            if (heightCheckInterval.current) {
              clearInterval(heightCheckInterval.current);
              heightCheckInterval.current = null;
            }
            isRestoringScroll.current = false;
            return;
          }
          
          const targetScroll = Math.max(0, currentHeight - windowHeight - 50);
          window.scrollTo({
            top: targetScroll,
            left: 0,
            behavior: 'instant'
          });
          
          // Stop if max attempts reached
          if (attemptCount >= maxAttempts) {
            if (heightCheckInterval.current) {
              clearInterval(heightCheckInterval.current);
              heightCheckInterval.current = null;
            }
            isRestoringScroll.current = false;
          }
        }
      };
      
      // Delay start to wait for Footer and other elements to render
      setTimeout(restoreScroll, 10);
      
      // Try every 100ms
      heightCheckInterval.current = setInterval(restoreScroll, 100);
      
      // Listen to DOM changes (lazy loading, skeleton, etc.) with throttle
      let mutationTimeout;
      const observer = new MutationObserver(() => {
        clearTimeout(mutationTimeout);
        mutationTimeout = setTimeout(restoreScroll, 50);
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Cleanup function
      setTimeout(() => {
        observer.disconnect();
        clearTimeout(mutationTimeout);
      }, 2500); // Stop listening after 2.5 seconds
      
      // Listen for all images to load
      const images = document.querySelectorAll('img');
      let loadedImages = 0;
      const totalImages = images.length;
      
      if (totalImages > 0) {
        const onImageLoad = () => {
          loadedImages++;
          if (loadedImages === totalImages) {
            // After all images loaded, try restoration one more time
            setTimeout(restoreScroll, 100);
          }
        };
        
        images.forEach(img => {
          if (img.complete) {
            onImageLoad();
          } else {
            img.addEventListener('load', onImageLoad, { once: true });
            img.addEventListener('error', onImageLoad, { once: true });
          }
        });
      }
    }
  };

  // Listen for any clicks, save current scroll position
  useEffect(() => {
    const handleClick = (event) => {
      // CRITICAL: Save current page's scroll position BEFORE any navigation
      // This ensures the scroll position is saved even if navigation happens very quickly
      const scrollY = window.scrollY || window.pageYOffset || 0;
      sessionStorage.setItem(`scroll_${fullPath}`, scrollY.toString());

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      let target = event.target;
      while (target && target !== document.body) {
        if (target.tagName === 'A' && target.href) {
          const anchorTarget = target.getAttribute('target');
          if (anchorTarget && anchorTarget !== '_self') {
            break;
          }
          if (target.hasAttribute('download')) {
            break;
          }
          try {
            const url = new URL(target.href);
            if (url.origin === window.location.origin) {
              const targetPath = url.pathname + url.search;
              const targetHash = url.hash;
              
              // Check if this is an anchor link - if so, allow browser default behavior
              if (targetHash) {
                return; // Let browser handle anchor navigation
              }
              
              // Mark navigation as starting
              isNavigating.current = true;
              navigationStartTime.current = Date.now();
              
              // Immediately remove the scroll position from sessionStorage to prevent any restoration
              sessionStorage.removeItem(`scroll_${targetPath}`);
              // Also set to 0 as backup
              sessionStorage.setItem(`scroll_${targetPath}`, '0');
              // Reset popstate flag to ensure this is treated as active navigation
              isPopStateNavigation.current = false;
              
              // CRITICAL: Set flag to skip scroll saving when we scroll to top
              // This prevents the scroll event listener from overwriting the saved scroll position
              skipScrollSaving.current = true;
              
              // IMPROVED: Delay scroll reset until navigation completes
              // This prevents flickering when user is still scrolling
              // Use requestIdleCallback if available, otherwise use setTimeout
              const scheduleScrollReset = () => {
                // Wait for navigation to complete (React Router navigation is async)
                // Use a small delay to let the navigation start, then reset scroll
                setTimeout(() => {
                  // Only reset if navigation is still in progress (not cancelled)
                  if (isNavigating.current) {
                    resetScrollToTop(false); // Use delayed reset to respect user scrolling
                    // Reset the flag after a delay to allow normal scroll saving on the new page
                    setTimeout(() => {
                      skipScrollSaving.current = false;
                      isNavigating.current = false;
                    }, 200);
                  }
                }, 50); // Small delay to let navigation start
              };
              
              if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(scheduleScrollReset, { timeout: 100 });
              } else {
                scheduleScrollReset();
              }
            }
          } catch (error) {
            // Ignore URL parsing errors
          }
          break;
        }
        target = target.parentElement;
      }
    };
    
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [fullPath]);

  // Detect user active scrolling
  useEffect(() => {
    let scrollTimer = null;
    
    const handleScroll = () => {
      const now = Date.now();
      lastUserScrollTime.current = now;
      
      // Mark user as scrolling
      if (!isUserScrolling.current) {
        isUserScrolling.current = true;
      }
      
      // Clear existing timer
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      
      // Mark user as stopped scrolling after 150ms of no scroll events
      scrollTimer = setTimeout(() => {
        isUserScrolling.current = false;
      }, 150);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { passive: true });
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      if (userScrollTimeout.current) {
        clearTimeout(userScrollTimeout.current);
      }
      if (scrollLockTimeout.current) {
        clearTimeout(scrollLockTimeout.current);
      }
    };
  }, []);

  // CRITICAL: Use pageshow event to catch browser scroll restoration BEFORE it's visible
  // pageshow fires before popstate and can catch browser's scroll restoration attempt
  useEffect(() => {
    const handlePageShow = (event) => {
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      const currentPath = window.location.pathname + window.location.search;
      
      // CRITICAL: ALWAYS reset scroll to 0 on pageshow if scroll position is > 10
      // This prevents browser from showing wrong page's scroll position during navigation
      // The actual restoration will happen later in path change useEffect
      if (currentScrollY > 10) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    };
    
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Listen for browser back/forward button
  useEffect(() => {
    const handlePopState = (event) => {
      // Ignore popstate if we just completed active navigation (to prevent false positives from React Router navigation)
      // BUT: Only ignore if it's within a short time window (200ms)
      if (justCompletedActiveNav.current) {
        justCompletedActiveNav.current = false;
        return;
      }
      
      // CRITICAL: Get target path from current location (popstate fires after navigation)
      // At this point, window.location already reflects the target page
      const targetPath = window.location.pathname + window.location.search;
      const targetHash = window.location.hash;
      
      // CRITICAL: Immediately reset scroll to 0 to prevent browser from restoring wrong position
      // This must happen BEFORE any other logic to prevent flash
      // ALWAYS reset to 0 on popstate if scroll position is > 10
      // This prevents browser from showing wrong page's scroll position (e.g., detail page's bottom position)
      // The actual restoration will happen later in path change useEffect
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      if (currentScrollY > 10) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
      
      // Save current page scroll position before navigating (use previousFullPath for the page we're leaving)
      if (previousFullPath.current && previousFullPath.current !== targetPath) {
        sessionStorage.setItem(`scroll_${previousFullPath.current}`, currentScrollY.toString());
      }
      
      isPopStateNavigation.current = true;
      
      // Check if URL has anchor - if so, allow browser default behavior
      if (targetHash || hasAnchor(targetPath)) {
        return;
      }
      
      // CRITICAL: Immediately lock scroll to prevent browser default restoration
      isScrollLocked.current = true;
      
      // CRITICAL: Immediately check target page's saved scroll position
      // This must happen synchronously to prevent browser's default scroll restoration
      const targetSavedScroll = sessionStorage.getItem(`scroll_${targetPath}`);
      const targetSavedScrollValue = targetSavedScroll ? parseInt(targetSavedScroll, 10) : 0;
      
      // Note: We already reset to 0 above, so we don't need to reset again here
      // The actual restoration will happen in path change useEffect
      
      // Don't unlock scroll here - let path change useEffect handle it
      // Scroll lock will be released when restoration starts in restoreScrollPosition
      // This ensures scroll is locked until restoration actually begins
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
      if (heightCheckInterval.current) {
        clearInterval(heightCheckInterval.current);
      }
      if (protectionPeriodTimeout.current) {
        clearTimeout(protectionPeriodTimeout.current);
      }
    };
  }, [fullPath]);


  // Immediate scroll reset on mount for new pages (before any restoration can happen)
  useEffect(() => {
    // Check if URL has anchor - if so, skip our custom logic
    if (hasAnchor(fullPath)) {
      return;
    }
    
    // Check if this is a new page (location key changed) and scroll position is not at top
    // This happens before path change detection, so we can catch early scroll restoration
    const currentScrollY = window.scrollY || window.pageYOffset || 0;
    const isNewPage = previousLocationKey.current !== locationKey;
    const hasPathChanged = previousFullPath.current !== fullPath;
    
    // For back/forward navigation, check if saved scroll is 0 and reset (but respect user scrolling)
    if (isPopStateNavigation.current && hasPathChanged) {
      const savedScroll = sessionStorage.getItem(`scroll_${fullPath}`);
      const savedScrollValue = savedScroll ? parseInt(savedScroll, 10) : 0;
      
      if (savedScrollValue === 0 && currentScrollY > 0) {
        // Delay reset to avoid flickering and respect user scrolling
        setTimeout(() => {
          if (!isUserScrolling.current) {
            resetScrollToTop(false);
          }
        }, 50);
      }
    }
    
    if (isNewPage && currentScrollY > 0 && !isPopStateNavigation.current) {
      // Delay reset to avoid flickering
      setTimeout(() => {
        if (!isUserScrolling.current) {
          resetScrollToTop(false);
        }
        // Remove from sessionStorage to prevent any further restoration
        sessionStorage.removeItem(`scroll_${fullPath}`);
      }, 50);
    }
  }, [locationKey, fullPath]);

  // Handle path/location changes - unified restoration logic here
  useEffect(() => {
    // Check if URL has anchor - if so, allow browser default behavior
    if (hasAnchor(fullPath)) {
      previousFullPath.current = fullPath;
      previousLocationKey.current = locationKey;
      isFirstMount.current = false;
      return;
    }
    
    const hasPathChanged = previousFullPath.current !== fullPath;
    const hasKeyChanged = previousLocationKey.current !== locationKey;

    // CRITICAL: Detect page refresh
    // Check if this is the first mount (page load/refresh)
    // On refresh, previousFullPath.current will be empty or equal to current path (if React Router hasn't updated it yet)
    // Also check performance.navigation.type for TYPE_RELOAD (1)
    // BUT: Only treat as refresh if we're truly on first mount AND path hasn't changed
    const isPageRefresh = isFirstMount.current && (
      (!previousFullPath.current || previousFullPath.current === fullPath) &&
      !hasPathChanged &&
      !hasKeyChanged &&
      (typeof performance !== 'undefined' && performance.navigation && performance.navigation.type === 1)
    );
    
    if (isPageRefresh) {
      // Clear all saved scroll positions on refresh
      Object.keys(sessionStorage).filter(k => k.startsWith('scroll_')).forEach(k => sessionStorage.removeItem(k));
      // Reset to top
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // Update refs
      previousFullPath.current = fullPath;
      previousLocationKey.current = locationKey;
      isFirstMount.current = false;
      return;
    }
    
    // Mark that we've passed first mount (only if we're not in a navigation)
    if (isFirstMount.current && (hasPathChanged || hasKeyChanged)) {
      isFirstMount.current = false;
    }

    // Reset check flag when path changes
    hasCheckedInitialScroll.current = false;

    // CRITICAL: Check if we have a saved scroll position for current path
    // This helps detect back navigation even if popstate wasn't triggered
    const savedScroll = sessionStorage.getItem(`scroll_${fullPath}`);
    const savedScrollValue = savedScroll ? parseInt(savedScroll, 10) : 0;
    const currentScrollY = window.scrollY || window.pageYOffset || 0;
    const hasSavedScroll = savedScrollValue > 0;
    const isAtTop = currentScrollY < 10;
    
    // CRITICAL: If we have saved scroll but we're at top, it might be a back navigation
    // BUT: Only treat as back navigation if previousFullPath was actually different from current path
    // This prevents false positives when detail page scroll position is saved but page is at top
    // We need to ensure previousFullPath is actually a different path (not just empty or same)
    const mightBeBackNavigation = hasSavedScroll && isAtTop && previousFullPath.current && previousFullPath.current !== fullPath && hasPathChanged;
    
    // CRITICAL: Also check if we have saved scroll and we're at top, even if path hasn't changed
    // BUT: Only if we have a valid previousFullPath that's different from current path
    // This handles the case where React Router updated previousFullPath before useEffect runs
    const hasSavedScrollButAtTop = hasSavedScroll && isAtTop && !hasPathChanged && !hasKeyChanged && previousFullPath.current && previousFullPath.current !== fullPath;
    
    // CRITICAL: If popstate navigation is active, force path change detection
    // This ensures restoration logic runs even if React Router hasn't updated location yet
    // Also check if we might have a back navigation (saved scroll but at top)
    // IMPORTANT: If isPopStateNavigation is true, we MUST process it even if path hasn't changed
    // This handles the case where React Router updated previousFullPath before useEffect runs
    const shouldProcessPathChange = hasPathChanged || hasKeyChanged || isPopStateNavigation.current || mightBeBackNavigation || hasSavedScrollButAtTop;
    
    // CRITICAL: If popstate navigation is active but path hasn't changed, 
    // we still need to process it (React Router may have updated previousFullPath already)

    // If path changed OR same path but a new navigation entry (location key changed) OR popstate navigation
    if (shouldProcessPathChange) {
      // Mark navigation as completed
      isNavigating.current = false;
      
      // Determine navigation type
      // Priority: if isPopStateNavigation is true AND we didn't just complete active nav, it's definitely back/forward
      // If we just completed active nav, ignore isPopStateNavigation (it might be a false positive from React Router)
      // Otherwise, if we have saved scroll and we're at top, it might be back navigation
      // Otherwise, if location.key changed, it's likely active navigation
      let isActiveNav;
      if (isPopStateNavigation.current && !justCompletedActiveNav.current) {
        // True back/forward navigation - restore scroll position
        isActiveNav = false;
      } else if (justCompletedActiveNav.current) {
        // Just completed active nav, ignore popstate - treat as active navigation
        isActiveNav = true;
      } else if (mightBeBackNavigation || hasSavedScrollButAtTop) {
        // Might be back navigation (saved scroll but at top) - treat as back navigation
        // BUT: Only if we have a valid previousFullPath that's different from current path
        // This prevents false positives when detail page has saved scroll but we're navigating to a different page
        if (previousFullPath.current && previousFullPath.current !== fullPath) {
          isActiveNav = false;
          // Set popstate flag to ensure restoration logic runs
          isPopStateNavigation.current = true;
        } else {
          // No valid previous path - treat as active navigation
          isActiveNav = true;
        }
      } else {
        // No popstate or popstate was ignored - check if key changed (new navigation)
        isActiveNav = hasKeyChanged || !isPopStateNavigation.current;
      }
      
      // Remove saved position only for active navigation (before restoration logic)
      // For back/forward navigation, we need to keep it for restoration
      if (isActiveNav) {
        sessionStorage.removeItem(`scroll_${fullPath}`);
        // Also set to 0 as backup to ensure it's cleared
        sessionStorage.setItem(`scroll_${fullPath}`, '0');
      }
      
      if (isActiveNav) {
        // Active navigation: immediately reset scroll to top
        // This ensures that when navigating to a page via link/button (not back button),
        // the page always starts at the top, regardless of any saved scroll position
        resetScrollToTop(false);
        
        // Active navigation: check scroll position once after a short delay
        // This handles cases where browser restored scroll position after navigation
        skipScrollSaving.current = true;
        
        // Reset flags
        isRestoringScroll.current = false;
        userScrolledManually.current = false;
        
        // Check scroll position after a delay to catch browser restoration
        // Only check once, don't force continuously, and respect user scrolling
        setTimeout(() => {
          if (!hasCheckedInitialScroll.current && !isUserScrolling.current) {
            const currentScroll = window.scrollY || window.pageYOffset || 0;
            const savedScroll = sessionStorage.getItem(`scroll_${fullPath}`);
            const savedScrollValue = savedScroll ? parseInt(savedScroll, 10) : 0;
            
            // If saved scroll is 0 but current scroll is not 0, reset once
            if (savedScrollValue === 0 && currentScroll > 10) {
              resetScrollToTop(false);
            }
            
            hasCheckedInitialScroll.current = true;
            skipScrollSaving.current = false;
          }
        }, 150); // Increased delay to let navigation settle
        
        // Ensure flag stays false
        isPopStateNavigation.current = false;
        // Set flag to ignore subsequent popstate events (React Router may trigger them)
        justCompletedActiveNav.current = true;
        // Clear the flag after a short delay to allow real back/forward navigation
        setTimeout(() => {
          justCompletedActiveNav.current = false;
        }, 200);
      } else {
        // Back/forward navigation: restore scroll position
        // CRITICAL: Only restore if we have a valid previousFullPath that's different from current path
        // This prevents false positives when detail page scroll position is saved but we're navigating to a different page
        if (!previousFullPath.current || previousFullPath.current === fullPath) {
          // Treat as active navigation - reset to top
          sessionStorage.removeItem(`scroll_${fullPath}`);
          resetScrollToTop(false);
          previousFullPath.current = fullPath;
          previousLocationKey.current = locationKey;
          return;
        }
        
        // CRITICAL: Before restoring target page, save the current page's scroll position (the page we're leaving)
        // This ensures we don't lose the scroll position of the page we're navigating away from
        const previousScrollY = window.scrollY || window.pageYOffset || 0;
        if (previousFullPath.current && previousFullPath.current !== fullPath) {
          // Save the scroll position of the page we're leaving
          sessionStorage.setItem(`scroll_${previousFullPath.current}`, previousScrollY.toString());
        }
        
        const savedScroll = sessionStorage.getItem(`scroll_${fullPath}`);
        const savedScrollValue = savedScroll ? parseInt(savedScroll, 10) : 0;
        
        // CRITICAL: Ensure scroll is locked during restoration check
        isScrollLocked.current = true;
        
        // CRITICAL: Immediately check and reset wrong scroll position (synchronous, no setTimeout)
        // This prevents the flash of wrong scroll position from browser's default restoration
        const currentScrollY = window.scrollY || window.pageYOffset || 0;
        
        // CRITICAL: If target page has saved scroll position, try to restore immediately if possible
        // This minimizes the flash from 0 to target position
        if (savedScrollValue > 0) {
          // Always reset to 0 first to ensure clean state
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          
          // Check if page height is sufficient for immediate restoration
          const currentHeight = document.documentElement.scrollHeight;
          const windowHeight = window.innerHeight;
          const canRestoreImmediately = savedScrollValue <= currentHeight - windowHeight + 200;
          
          if (canRestoreImmediately) {
            // Page height is sufficient - try to restore immediately to minimize flash
            // Use requestAnimationFrame to ensure DOM is ready, but do it as soon as possible
            requestAnimationFrame(() => {
              // Single RAF should be enough - we want to restore as quickly as possible
              window.scrollTo({
                top: savedScrollValue,
                left: 0,
                behavior: 'instant'
              });
            });
          }
        } else if (savedScrollValue === 0 && currentScrollY > 10) {
          // Target page should be at top, but browser restored some position
          // Immediately reset to prevent flash
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
        
        // Unlock scroll when restoration starts (in restoreScrollPosition)
        // Keep locked until restoration begins to prevent any scroll events from interfering
        // The lock will be released in restoreScrollPosition when restoration actually starts
        
        // Reset all flags on path change
        // CRITICAL: Reset user scrolling flags BEFORE starting restoration
        // This ensures programmatic scrolls (like resetting to 0) don't interfere with restoration
        isRestoringScroll.current = false;
        userScrolledManually.current = false;
        isUserScrolling.current = false;
        
        // Clear previous timers
        if (restoreTimeoutRef.current) {
          clearTimeout(restoreTimeoutRef.current);
        }
        if (heightCheckInterval.current) {
          clearInterval(heightCheckInterval.current);
          heightCheckInterval.current = null;
        }
        if (protectionPeriodTimeout.current) {
          clearTimeout(protectionPeriodTimeout.current);
        }
        
        // CRITICAL: Start protection period (50ms) to allow user to start scrolling
        // Reduced from 100ms to minimize flash - we already reset to 0 immediately above
        // If user scrolls during this period, cancel restoration
        isInProtectionPeriod.current = true;
        
        protectionPeriodTimeout.current = setTimeout(() => {
          isInProtectionPeriod.current = false;
        }, 50);
        
        // If saved position is 0, reset to top (but respect user scrolling and protection period)
        if (savedScrollValue === 0) {
          // Wait for protection period to end, then check if user scrolled
          setTimeout(() => {
            // Check if user scrolled during protection period
            if (userScrolledManually.current || isUserScrolling.current) {
              return;
            }
            
            if (!isUserScrolling.current) {
              resetScrollToTop(false);
            }
            
            // Check once after a delay to catch browser restoration
            setTimeout(() => {
              if (!hasCheckedInitialScroll.current) {
                const currentScroll = window.scrollY || window.pageYOffset || 0;
                if (currentScroll > 10 && !isUserScrolling.current && !userScrolledManually.current) {
                  resetScrollToTop(false);
                }
                hasCheckedInitialScroll.current = true;
              }
            }, 100);
          }, 50); // Wait for protection period
        } else {
          // Saved position is > 0, delay restoration to wait for React rendering and React Query cache loading
          // Reduced delay to minimize flash - we already reset to 0 immediately above
          // If we attempted immediate restoration above, this will fine-tune the position
          restoreTimeoutRef.current = setTimeout(() => {
            // CRITICAL: Before restoring, check if user scrolled during protection period
            // But also reset flags to ensure programmatic scrolls don't interfere
            const userScrolledDuringProtection = userScrolledManually.current || isUserScrolling.current;
            
            if (userScrolledDuringProtection) {
              return;
            }
            
            // CRITICAL: Reset user scrolling flags one more time before restoration
            // This ensures any programmatic scrolls (like resetting to 0) don't interfere
            userScrolledManually.current = false;
            isUserScrolling.current = false;
            
            // Only restore if user hasn't scrolled
            if (!isUserScrolling.current && !userScrolledManually.current) {
              restoreScrollPosition(fullPath);
              // Reset flag after restoration complete
              setTimeout(() => {
                isPopStateNavigation.current = false;
              }, 100);
            }
          }, 50); // Reduced from 100ms to minimize flash
        }
        
        // Reset flag after a short delay
        setTimeout(() => {
          isPopStateNavigation.current = false;
        }, 100);
      }
    }

    previousFullPath.current = fullPath;
    previousLocationKey.current = locationKey;
  }, [fullPath, locationKey]);

  // Listen to scroll events, save position in real-time + detect user manual scrolling
  useEffect(() => {
    let ticking = false;
    let lastScrollY = window.scrollY || 0;
    let lastScrollDirection = null; // 'up' or 'down'
    let scrollDirectionChangeCount = 0;
    
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY || window.pageYOffset || 0;
          const scrollDelta = scrollY - lastScrollY;
          const absDelta = Math.abs(scrollDelta);
          
          // CRITICAL: Skip user scrolling detection if scroll is locked or during restoration
          // This prevents programmatic scrolls (like resetting to 0) from being detected as user scrolling
          const shouldDetectUserScrolling = !isScrollLocked.current && !isRestoringScroll.current;
          
          // CRITICAL: Detect user scrolling regardless of restoration state
          // This ensures we catch user scrolling immediately, even during restoration
          // BUT: Skip detection if scroll is locked (programmatic scroll) or during restoration
          if (shouldDetectUserScrolling && absDelta > 5) { // Minimum threshold to avoid noise
            const currentDirection = scrollDelta > 0 ? 'down' : 'up';
            
            // Detect direction changes (indicates active user scrolling)
            if (lastScrollDirection && lastScrollDirection !== currentDirection) {
              scrollDirectionChangeCount++;
              // If direction changed multiple times, it's definitely user scrolling
              if (scrollDirectionChangeCount >= 2) {
                userScrolledManually.current = true;
                // If we're restoring, stop immediately
                if (isRestoringScroll.current) {
                  if (heightCheckInterval.current) {
                    clearInterval(heightCheckInterval.current);
                    heightCheckInterval.current = null;
                  }
                  isRestoringScroll.current = false;
                }
              }
            } else {
              scrollDirectionChangeCount = 0;
            }
            
            lastScrollDirection = currentDirection;
            
            // Also detect significant scroll distance (user actively scrolling)
            if (absDelta > 15) {
              userScrolledManually.current = true;
              // If we're restoring, stop immediately
              if (isRestoringScroll.current) {
                if (heightCheckInterval.current) {
                  clearInterval(heightCheckInterval.current);
                  heightCheckInterval.current = null;
                }
                isRestoringScroll.current = false;
              }
            }
          } else if (!shouldDetectUserScrolling) {
            // Reset direction tracking when scroll is locked or during restoration
            // This prevents programmatic scrolls from affecting user scrolling detection
            scrollDirectionChangeCount = 0;
            lastScrollDirection = null;
          }
          
          // Skip saving if scroll is locked (during immediate restoration check)
          if (isScrollLocked.current) {
            ticking = false;
            return;
          }
          
          // Skip saving if we're in the middle of active navigation
          if (skipScrollSaving.current) {
            ticking = false;
            return;
          }
          
          // Skip saving if popstate navigation is still active (within the reset window)
          // BUT: Don't skip if user is actively scrolling (they might be scrolling during back navigation)
          if (isPopStateNavigation.current && !userScrolledManually.current) {
            ticking = false;
            return;
          }
          
          sessionStorage.setItem(`scroll_${fullPath}`, scrollY.toString());
          
          lastScrollY = scrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledScroll, { passive: true });
      // Don't save on unmount! Because:
      // 1. Position already saved when link clicked
      // 2. During unmount, page might be navigating, scrollY might be 0, overwriting correct value
    };
  }, [fullPath]);

  return null;
};

export default InfiniteScrollRestoration;

